// src/pages/servicos/cadastros/EquipamentoClienteSearch.js
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FaSortDown, FaSortUp } from 'react-icons/fa';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalTable from '../../../components/modalForm/ModalTable';
import LazyLoading from '../../../components/modalForm/LazyLoading';
import { useEquipamentoCliente } from '../../../contexts/servicos/EquipamentoClienteContext';
import ModalSearchBar from '../../../components/modalForm/ModalSearchBar';
import { ModalActionButton } from '../../../components/modalForm/ModalActions';
import ModalSelectButton from '../../../components/modalForm/ModalSelectButton';

const EquipamentoClienteSearch = ({ show, clienteId, onHide, onSelectEquipamentoCliente }) => {
  const { equipamentos } = useEquipamentoCliente();
  const [filtro, setFiltro] = useState('');

  const [ordenacao, setOrdenacao] = useState({ campo: 'id', direcao: 'asc' });
  const inputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setFiltro('');

      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [show]);

  const equipamentosFiltrados = useMemo(() => {
    const texto = filtro.trim().toLocaleLowerCase('pt-BR');
    const equipamentosDoCliente = clienteId
      ? equipamentos.filter(equipamento => Number(equipamento.clienteId) === Number(clienteId))
      : equipamentos;
    const filtrados = texto
      ? equipamentosDoCliente.filter(equipamento => [
        equipamento.id,
        equipamento.clienteNome,
        equipamento.tipoEquipamentoDescricao,
        equipamento.descricao,
        equipamento.marcaDescricao,
        equipamento.modelo,
        equipamento.numeroSerie,
        equipamento.patrimonio,
      ].some(valor => String(valor || '').toLocaleLowerCase('pt-BR').includes(texto)))
      : equipamentosDoCliente;

    return [...filtrados].sort((a, b) => {
      const valorA = a[ordenacao.campo] ?? '';
      const valorB = b[ordenacao.campo] ?? '';
      const comparacao = ordenacao.campo === 'id'
        ? Number(valorA) - Number(valorB)
        : String(valorA).localeCompare(String(valorB), 'pt-BR');
      return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
    });
  }, [equipamentos, clienteId, filtro, ordenacao]);

  if (!show) return null;

  const ordenar = campo => {
    setOrdenacao(anterior => ({
      campo,
      direcao: anterior.campo === campo && anterior.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const iconeOrdenacao = campo => ordenacao.campo === campo
    ? (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)
    : null;

  const selecionar = equipamento => {
    onSelectEquipamentoCliente(equipamento);
    onHide();
  };

  return (
    <ModalContainer show={show} largura="980px" zIndex={1060} variant="busca" onHide={onHide}>
      <ModalHeader title="Buscar Equipamento do Cliente" onHide={onHide}
        subtitle={clienteId ? `Exibindo somente equipamentos do cliente #${clienteId}` : undefined} />
      <ModalBody>
        <div className="mb-3">
          <ModalSearchBar
            inputRef={inputRef}
            type="text"

            placeholder="Filtrar por cliente, equipamento, marca, modelo, série ou patrimônio..."
            value={filtro}
            onChange={e => {
              setFiltro(e.target.value);

            }}
          />
        </div>
        <LazyLoading items={equipamentosFiltrados} resetKey={JSON.stringify([show, filtro, ordenacao])}>
        {({ containerRef, handleScroll, visibleItems }) => (
        <ModalTable containerRef={containerRef} onContainerScroll={handleScroll}>
          <thead>
            <tr>
              {[
                ['id', 'Código'],
                ['clienteNome', 'Cliente'],
                ['tipoEquipamentoDescricao', 'Tipo'],
                ['descricao', 'Equipamento'],
                ['marcaDescricao', 'Marca'],
              ].map(([campo, titulo]) => (
                <th key={campo} className={campo === 'id' ? 'text-center' : 'text-start'} aria-sort={ordenacao.campo === campo ? (ordenacao.direcao === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    onClick={() => ordenar(campo)}
                    style={{ fontWeight: 'bold', background: 'none', border: 'none' }}
                    tabIndex={0}
                  >
                    {titulo} {iconeOrdenacao(campo)}
                  </button>
                </th>
              ))}
              <th className="text-center modal-select-column">Selecionar</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map(equipamento => (
              <tr key={equipamento.id} onDoubleClick={() => selecionar(equipamento)}>
                <td className="text-end">{equipamento.id}</td>
                <td>{equipamento.clienteNome}</td>
                <td>{equipamento.tipoEquipamentoDescricao}</td>
                <td>{equipamento.descricao}</td>
                <td>{equipamento.marcaDescricao || '-'}</td>
                <td className="text-center modal-select-column">
                  <ModalSelectButton type="button" className="btn btn-outline-secondary btn-sm" onClick={() => selecionar(equipamento)} label={equipamento.descricao} />
                </td>
              </tr>
            ))}
            {visibleItems.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-muted">Nenhum equipamento encontrado.</td>
              </tr>
            )}
          </tbody>
        </ModalTable>
        )}
        </LazyLoading>
      </ModalBody>
      <ModalFooter>

        <ModalActionButton type="button" className="btn btn-secondary btn-sm" onClick={onHide} action="close">Fechar</ModalActionButton>
      </ModalFooter>
    </ModalContainer>
  );
};

export default EquipamentoClienteSearch;
